import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Switch } from '@/components/ui/switch';
import useBrains from '@/hooks/useBrains';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { ImprovementsIntro } from './components/ImprovementsIntro';
import { ImprovementsList } from './components/ImprovementsList';
import { useImprovements } from './hooks/useImprovements';
import type { ImprovementStatus } from './types';
import { filterByStatus } from './utils';

export const ImprovementsPage = () => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const [statusFilter, setStatusFilter] = useState<ImprovementStatus | 'all'>('pending');
  const { brain, getStatus, updateBrain, improvementsEnabled } = useBrains(brainId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const {
    improvements,
    hasAny,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useImprovements(brainId);

  // Optimistic state for the improvements toggle (mirrors the Insights toggle)
  const [optimisticImprovementsEnabled, setOptimisticImprovementsEnabled] = useState<
    boolean | null
  >(null);

  const handleImprovementsToggle = useCallback(
    (checked: boolean) => {
      if (brain) {
        setOptimisticImprovementsEnabled(checked);

        // Orchestrator can reject the enable; the mutation's global onError toast surfaces it.
        updateBrain(
          {
            brain_id: brain.brain_id,
            metadata: { ...brain.metadata, improvements_enabled: checked },
          },
          {
            onError: () => {
              setOptimisticImprovementsEnabled(null);
            },
          }
        );
      }
    },
    [brain, updateBrain]
  );

  // Reset optimistic state when the actual value catches up (success case)
  useEffect(() => {
    if (
      optimisticImprovementsEnabled !== null &&
      optimisticImprovementsEnabled === improvementsEnabled
    ) {
      setOptimisticImprovementsEnabled(null);
    }
  }, [improvementsEnabled, optimisticImprovementsEnabled]);

  // An in-flight toggle for one agent must not carry over to another
  useEffect(() => {
    setOptimisticImprovementsEnabled(null);
  }, [brainId]);

  const displayImprovementsEnabled = optimisticImprovementsEnabled ?? improvementsEnabled;

  useSegments(PageName.IMPROVEMENTS);

  const visibleImprovements = useMemo(
    () => filterByStatus(improvements, statusFilter),
    [improvements, statusFilter]
  );

  return (
    <div className={TABLE_LAYOUT_FILL_CLASSES}>
      <ImprovementsIntro />
      <div className="mb-5 flex justify-end">
        <Switch
          disabled={!canWrite || !brain}
          label={t('improvements.toggle_label')}
          id="improvements-toggle"
          checked={displayImprovementsEnabled}
          onCheckedChange={handleImprovementsToggle}
          aria-label={t('improvements.toggle_label')}
        />
      </div>
      <ImprovementsList
        brainId={brainId}
        improvements={visibleImprovements}
        hasAny={hasAny}
        // Wait for brain metadata too (pending only, so a failed brain fetch
        // can't strand the list in the skeleton), or the disabled empty state
        // flashes for an enabled agent
        isLoading={isLoading || (!brain && getStatus === 'pending')}
        isError={isError}
        improvementsEnabled={displayImprovementsEnabled}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
      />
    </div>
  );
};
